import React, { useEffect, useState } from "react";
import axios from "axios";

const SERVER_URL = "https://allinone-clone-7d5d795e98ce.herokuapp.com";

const BannerSection = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBanner = async () => {
      try {
        const res = await axios.get(`${SERVER_URL}/api/banner-section`);
        if (res.data.success) {
          setData(res.data.data);
        }
      } catch (err) {
        console.error("Failed to load banner:", err);
        setError("Failed to load content");
      } finally {
        setLoading(false);
      }
    };
    fetchBanner();
  }, []);

  // Helper to convert YouTube watch URLs to embed URLs
  const getEmbedUrl = (url) => {
    if (!url) return "";
    if (url.includes("embed")) return url;
    // Convert standard watch URL to embed
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/);
    return match ? `https://www.youtube.com/embed/${match[1]}` : url;
  };

  // Loading State
  if (loading)
    return (
      <div
        style={{
          minHeight: "600px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#0f172a",
          color: "#94a3b8",
        }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );

  // Error or Inactive State
  if (error || !data || !data.isActive) return null;

  return (
    <>
      <section className="hero-section position-relative py-5 overflow-hidden">
        {/* Modern Dark Backgrounds with subtle ambient glows */}
        <div className="bg-absolute bg-dark-base"></div>
        <div className="ambient-glow glow-primary"></div>
        <div className="ambient-glow glow-secondary"></div>
        <div className="ambient-glow glow-tertiary"></div>

        <div className="container-xl position-relative z-1 mt-5 pt-lg-5 pb-5 px-xl-5">
          <div className="row align-items-center">
            {/* Mockup / Video Section (Right side on desktop) */}
            {data.youtubeUrl && (
              <div
                className="col-lg-6 mb-5 mb-lg-0 order-2 order-lg-2 animate-fade-in-up"
                style={{ animationDelay: "0.5s" }}
              >
                <div className="hero-video-wrapper rounded-4 glass-panel position-relative mx-auto border border-white-10 shadow-premium">
                  {/* Browser Mockup Header */}
                  <div className="browser-header d-flex align-items-center px-4 py-3 border-bottom border-white-10 glass-header rounded-top-4">
                    <div className="d-flex gap-2">
                      <div className="rounded-circle window-btn close-btn"></div>
                      <div className="rounded-circle window-btn min-btn"></div>
                      <div className="rounded-circle window-btn max-btn"></div>
                    </div>
                    <div
                      className="mx-auto rounded-2 bg-dark-input border border-white-10 px-4 py-1 text-secondary text-sm d-flex align-items-center justify-content-center"
                      style={{
                        width: "50%",
                        maxWidth: "350px",
                        height: "28px",
                      }}
                    >
                      <i
                        className="fa-solid fa-lock me-2 text-muted"
                        style={{ fontSize: "10px" }}
                      ></i>
                      <span className="opacity-75">
                        {window.location.hostname}
                      </span>
                    </div>
                    <div style={{ width: "42px" }}></div>{" "}
                    {/* Spacer for centering */}
                  </div>

                  <div className="p-2 p-md-3 bg-dark-inner rounded-bottom-4">
                    <div
                      style={{
                        position: "relative",
                        width: "100%",
                        paddingBottom: "56.25%",
                        height: 0,
                        overflow: "hidden",
                        borderRadius: "8px",
                        boxShadow: "inset 0 0 20px rgba(0,0,0,0.5)",
                      }}
                    >
                      <iframe
                        src={getEmbedUrl(data.youtubeUrl)}
                        title="AI Content Suite Demo"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          border: 0,
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Content Section (Left side on desktop) */}
            <div
              className={
                data.youtubeUrl
                  ? "col-lg-6 text-center text-lg-start order-1 order-lg-1 pe-lg-5"
                  : "col-xl-9 col-lg-10 mx-auto text-center"
              }
            >
              <div className="hero-content mb-5 mb-lg-0">
                {data.badgeText && (
                  <div className="badge-wrapper mb-4 animate-fade-in-up">
                    <span className="badge-premium d-inline-flex align-items-center px-3 py-2 rounded-pill border border-white-10 glass-panel tracking-wider text-uppercase shadow-sm">
                      <span className="badge-dot me-2"></span>
                      <span className="text-secondary fw-semibold text-xs">
                        {data.badgeText}
                      </span>
                    </span>
                  </div>
                )}

                {/* Title */}
                <h1
                  className="display-3 fw-bolder mb-4 text-white animate-fade-in-up title-glow"
                  style={{
                    letterSpacing: "-0.04em",
                    lineHeight: "1.15",
                    animationDelay: "0.1s",
                  }}
                  dangerouslySetInnerHTML={{ __html: data.mainTitle || "" }}
                />

                {/* Description */}
                <p
                  className={`lead text-secondary mb-5 animate-fade-in-up fs-5 ${data.youtubeUrl ? "" : "px-xl-5 mx-xl-5"}`}
                  style={{ lineHeight: "1.7", animationDelay: "0.2s" }}
                >
                  {data.description}
                </p>

                {/* Buttons */}
                <div
                  className={`d-flex flex-column flex-sm-row gap-3 mt-4 mt-lg-5 animate-fade-in-up justify-content-center ${data.youtubeUrl ? "justify-content-lg-start" : ""}`}
                  style={{ animationDelay: "0.3s" }}
                >
                  {data.primaryButton && (
                    <a
                      href={data.primaryButton.link}
                      className="btn btn-premium-primary btn-lg px-4 px-md-5 py-3 rounded-pill fw-bold d-inline-flex justify-content-center align-items-center transition-all text-nowrap"
                    >
                      {data.primaryButton.text}{" "}
                      <i className="fa-solid fa-arrow-right ms-2 fs-6" />
                    </a>
                  )}

                  {data.secondaryButton && (
                    <a
                      href={data.secondaryButton.link}
                      className="btn btn-premium-secondary btn-lg px-4 px-md-5 py-3 rounded-pill fw-bold d-inline-flex justify-content-center align-items-center transition-all text-nowrap"
                    >
                      {data.secondaryButton.text}{" "}
                      <i className="fa-solid fa-play ms-2 fs-6" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <style jsx>{`
          .hero-section {
            min-height: 90vh;
            display: flex;
            align-items: center;
            background-color: #020617; /* Slate 950 base */
            font-family: "Inter", system-ui, sans-serif;
          }

          .bg-absolute {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            z-index: 0;
          }

          .bg-dark-base {
            background-color: #020617;
            background-image:
              linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px),
              linear-gradient(
                90deg,
                rgba(255, 255, 255, 0.03) 1px,
                transparent 1px
              );
            background-size: 40px 40px;
            background-position: center center;
            opacity: 0.6;
            mask-image: radial-gradient(
              circle at center,
              black 40%,
              transparent 80%
            );
            -webkit-mask-image: radial-gradient(
              circle at center,
              black 40%,
              transparent 80%
            );
          }

          .ambient-glow {
            position: absolute;
            border-radius: 50%;
            filter: blur(120px);
            z-index: 0;
            opacity: 0.5;
            pointer-events: none;
            animation: float-glow 20s ease-in-out infinite alternate;
          }

          .glow-primary {
            width: 700px;
            height: 700px;
            background: rgba(99, 102, 241, 0.15); /* Indigo */
            top: -100px;
            left: 5%;
            animation-delay: 0s;
          }

          .glow-secondary {
            width: 600px;
            height: 600px;
            background: rgba(139, 92, 246, 0.15); /* Violet */
            bottom: -200px;
            right: 5%;
            animation-delay: -10s;
          }

          .glow-tertiary {
            width: 500px;
            height: 500px;
            background: rgba(14, 165, 233, 0.1); /* Sky blue */
            top: 40%;
            left: 50%;
            transform: translate(-50%, -50%);
            animation-duration: 25s;
            animation-delay: -5s;
          }

          @keyframes float-glow {
            0% {
              transform: translate(0, 0) scale(1);
            }
            33% {
              transform: translate(30px, -50px) scale(1.1);
            }
            66% {
              transform: translate(-20px, 20px) scale(0.9);
            }
            100% {
              transform: translate(0, 0) scale(1);
            }
          }

          .glass-panel {
            background: rgba(15, 23, 42, 0.4);
            backdrop-filter: blur(16px);
            -webkit-backdrop-filter: blur(16px);
          }

          .glass-header {
            background: rgba(15, 23, 42, 0.6);
            backdrop-filter: blur(20px);
          }

          .border-white-10 {
            border-color: rgba(255, 255, 255, 0.08) !important;
          }

          .text-secondary {
            color: #94a3b8 !important; /* Slate 400 */
          }

          .text-xs {
            font-size: 0.75rem;
          }

          .text-sm {
            font-size: 0.875rem;
          }

          .tracking-wider {
            letter-spacing: 0.05em;
          }

          .badge-premium {
            background: linear-gradient(
              180deg,
              rgba(30, 41, 59, 0.7) 0%,
              rgba(15, 23, 42, 0.7) 100%
            );
            box-shadow:
              0 4px 12px rgba(0, 0, 0, 0.1),
              inset 0 1px 0 rgba(255, 255, 255, 0.1);
          }

          .badge-dot {
            width: 8px;
            height: 8px;
            background-color: #8b5cf6; /* Violet 500 */
            border-radius: 50%;
            box-shadow: 0 0 8px #8b5cf6;
            animation: pulse-dot 2s infinite;
          }

          @keyframes pulse-dot {
            0% {
              box-shadow: 0 0 0 0 rgba(139, 92, 246, 0.4);
            }
            70% {
              box-shadow: 0 0 0 6px rgba(139, 92, 246, 0);
            }
            100% {
              box-shadow: 0 0 0 0 rgba(139, 92, 246, 0);
            }
          }

          .title-glow {
            text-shadow: 0 0 40px rgba(255, 255, 255, 0.1);
          }

          /* Global styling for strong tags inside title if they exist */
          .hero-content h1 :global(strong),
          .hero-content h1 :global(b),
          .hero-content h1 :global(span.text-gradient) {
            background: linear-gradient(135deg, #818cf8, #c084fc);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            color: transparent;
            display: inline-block;
          }

          .btn-premium-primary {
            background: #ffffff;
            color: #020617; /* Slate 950 text */
            border: 1px solid transparent;
            box-shadow: 0 4px 14px 0 rgba(255, 255, 255, 0.25);
            letter-spacing: 0.02em;
          }

          .btn-premium-primary:hover {
            background: #f8fafc;
            color: #020617;
            transform: translateY(-2px);
            box-shadow: 0 6px 20px rgba(255, 255, 255, 0.35);
          }

          .btn-premium-secondary {
            background: rgba(255, 255, 255, 0.03);
            color: #f8fafc;
            border: 1px solid rgba(255, 255, 255, 0.15);
            backdrop-filter: blur(10px);
            letter-spacing: 0.02em;
          }

          .btn-premium-secondary:hover {
            background: rgba(255, 255, 255, 0.08);
            color: white;
            transform: translateY(-2px);
            border-color: rgba(255, 255, 255, 0.3) !important;
            box-shadow: 0 4px 14px 0 rgba(0, 0, 0, 0.2);
          }

          .hero-video-wrapper {
            background: #0f172a; /* Slate 900 */
            transform: scale(1) translateY(0);
            transition:
              transform 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275),
              box-shadow 0.6s ease,
              border-color 0.6s ease;
            max-width: 1000px;
          }

          .shadow-premium {
            box-shadow:
              0 25px 50px -12px rgba(0, 0, 0, 0.7),
              0 0 0 1px rgba(255, 255, 255, 0.05),
              0 0 30px rgba(79, 70, 229, 0.1);
          }

          .hero-video-wrapper:hover {
            transform: scale(1.04) translateY(-12px);
            box-shadow:
              0 40px 80px -12px rgba(0, 0, 0, 0.9),
              0 0 0 1px rgba(255, 255, 255, 0.2),
              0 0 60px rgba(139, 92, 246, 0.3);
            border-color: rgba(255, 255, 255, 0.2) !important;
          }

          .bg-dark-input {
            background: rgba(2, 6, 23, 0.6); /* Slate 950 */
            box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.5);
          }

          .bg-dark-inner {
            background: #020617;
          }

          .window-btn {
            width: 12px;
            height: 12px;
            transition: opacity 0.2s ease;
          }

          .window-btn:hover {
            opacity: 0.8;
          }

          .close-btn {
            background: #ef4444;
            border: 1px solid #dc2626;
          }
          .min-btn {
            background: #f59e0b;
            border: 1px solid #d97706;
          }
          .max-btn {
            background: #10b981;
            border: 1px solid #059669;
          }

          .animate-fade-in-up {
            animation: fadeInUp 0.8s cubic-bezier(0.22, 1, 0.36, 1) forwards;
            opacity: 0;
            transform: translateY(30px);
          }

          @keyframes fadeInUp {
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}</style>
      </section>
    </>
  );
};

export default BannerSection;
